import { useCallback, useRef, useState } from 'react';
import { prepareImage, uploadInspectionPhoto, removeInspectionPhoto, UploadError } from '../../helpers/inspectionUpload';
import {
  fromServer, pendingEntries, patchItem, replaceLocal, removeServerPhoto, removeLocalItem, isBusy, readyPhotos
} from '../../helpers/inspectionPhotoState';

// State of one series of photos (vehicle, damage or dispute attachments): server photos + uploads in progress or failed.
// `ensureReady()` resolves true when the inspection exists (the page creates the draft on the first photo), false to abort.
// `onVersion(version)` receives the version returned by the server after an upload / removal (the page keeps expectedVersion in sync).
export function usePhotoSeries({ reservationId, type, kind, initial, ensureReady, onVersion, onError, upload = uploadInspectionPhoto, remove = removeInspectionPhoto, prepare = prepareImage }) {
  const [items, setItems] = useState(() => fromServer(initial));
  const counter = useRef(0);
  const queue = useRef(Promise.resolve());

  const patch = useCallback((localId, next) => setItems((current) => patchItem(current, localId, next)), []);

  const replaceWith = useCallback((localId, photos) => setItems((current) => replaceLocal(current, localId, photos, kind)), [kind]);

  const sendOne = useCallback(async (localId, file) => {
    patch(localId, { status: 'uploading', errorKey: null });
    try {
      if (ensureReady && !(await ensureReady())) { patch(localId, { status: 'failed', errorKey: 'inspUploadFailed', file }); return; }
      const prepared = await prepare(file);
      const result = await upload({ reservationId, type, kind, file: prepared });
      replaceWith(localId, result.photos && result.photos.length ? result.photos : [result]);
      if (onVersion && result.version !== null && result.version !== undefined) onVersion(result.version);
    } catch (error) {
      const failure = error instanceof UploadError ? error : new UploadError('failed', null);
      patch(localId, { status: 'failed', errorKey: failure.messageKey, file });
      if (onError) onError(failure);
    }
  }, [reservationId, type, kind, ensureReady, onVersion, onError, upload, prepare, patch, replaceWith]);

  // Files are sent one after the other: each upload may create the next version of the record on the server.
  const add = useCallback((files) => {
    const pending = pendingEntries(files, () => `n${counter.current += 1}`);
    setItems((current) => [...current, ...pending]);
    pending.forEach((entry) => { queue.current = queue.current.then(() => sendOne(entry.localId, entry.file)); });
  }, [sendOne]);

  const retry = useCallback((item) => {
    if (item.file) queue.current = queue.current.then(() => sendOne(item.localId, item.file));
  }, [sendOne]);

  const removeItem = useCallback(async (item) => {
    if (item.id === undefined || item.id === null) {
      setItems((current) => removeLocalItem(current, item.localId));
      return true;
    }
    try {
      const result = await remove({ photoId: item.id });
      setItems((current) => removeServerPhoto(current, item.id));
      if (onVersion && result && result.version !== null && result.version !== undefined) onVersion(result.version);
      return true;
    } catch (error) {
      if (onError) onError(error instanceof UploadError ? error : new UploadError('failed', null));
      return false;
    }
  }, [remove, onVersion, onError]);

  const reset = useCallback((photos) => setItems(fromServer(photos)), []);
  const busy = isBusy(items);
  const readyItems = readyPhotos(items);
  return { items, add, retry, remove: removeItem, reset, busy, readyItems };
}

export default usePhotoSeries;
